import React, { useState, useEffect } from 'react';
import { Grid, Typography, Card, CardContent, Box, Alert, Divider, Button, AlertTitle } from '@mui/material';
import { CloudUpload, Upload } from '@mui/icons-material';
import InputFormContainer from '@/components/chat/InputFormContainer';
import FileUploader from '@/components/FileUploader';
import LoadLatestResultsButton from '../LoadLatestResultsButton';
import KeywordStorageStatus from './KeywordStorageStatus';
import KeywordReadinessStatus from './KeywordReadinessStatus';
import ClientFileUploadModal from '../ClientFileUploadModal';
import { snowflakeService } from '../../services/snowflakeService';
import { useToast } from '../../context/ToastContext';
import { useGetLatestResults } from '../../services/module1Service';
import { useWorkflowContext } from '../../context/WorkflowContext';

interface AnalysisInputsProps {
  onAnalysisComplete: (businessInfo: any) => Promise<void>;
  onFileUpload: (file: File) => Promise<string>;
  isUploading: boolean;
  isProcessing: boolean; // Add isProcessing prop
  uploadedFilePath: string | null;
  hasUploadedFile: boolean;
  isUsingDatabaseApproach: boolean;
  onResultsLoaded?: (results: any) => void; // Add this prop for loading latest results
  onShowResults?: () => void; // Add this prop for showing existing results
  storedKeywordCount?: number;
  totalKeywordCount?: number;
  clientId?: string;
  clientName?: string;
  workflowId?: string;
}

const AnalysisInputs: React.FC<AnalysisInputsProps> = ({
  onAnalysisComplete,
  onFileUpload,
  isUploading,
  isProcessing, // Add isProcessing to props
  uploadedFilePath,
  hasUploadedFile,
  isUsingDatabaseApproach,
  onResultsLoaded,
  onShowResults,
  storedKeywordCount,
  totalKeywordCount,
  clientId,
  clientName,
  workflowId
}) => {
  const { showToast } = useToast();
  const { moduleStatuses, workflowName, workflow } = useWorkflowContext();
  const getLatestResults = useGetLatestResults();
  const [isLoadingKeywords, setIsLoadingKeywords] = useState(false);
  const [hasSnowflakeKeywords, setHasSnowflakeKeywords] = useState(false);
  const [snowflakeBlobPath, setSnowflakeBlobPath] = useState<string | null>(null);
  const [snowflakeKeywordCount, setSnowflakeKeywordCount] = useState<number | null>(null);
  const [autoLoadAttempted, setAutoLoadAttempted] = useState(false);
  const [fileUploadModalOpen, setFileUploadModalOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Check if there are completed Module 1 results available
  const hasModule1Results = moduleStatuses?.latest_status_by_type?.['module1']?.status === 'completed';

  // Extract the results property if nested (same logic as LoadLatestResultsButton)
  const extractRawResults = (data: any): any => {
    // Case 1: If data already has topics array, return it directly
    if (data.topics && Array.isArray(data.topics)) {
      return data;
    }
    
    // Case 2: If data has results with topics, extract them
    if (data.results && data.results.topics && Array.isArray(data.results.topics)) {
      return {
        topics: data.results.topics,
        missing_themes: data.results.missing_themes || []
      };
    }
    
    // Case 3: If data has nested content with topics, extract them
    if (data.results?.content?.topics && Array.isArray(data.results.content.topics)) {
      return {
        topics: data.results.content.topics,
        missing_themes: data.results.content.missing_themes || []
      };
    }
    
    // No transformations applied, just ensure proper structure
    return data;
  };

  // Auto-load latest results on first load
  const autoLoadLatestResults = async () => {
    if (!clientId || !workflowId || !onResultsLoaded || autoLoadAttempted) {
      return;
    }

    setAutoLoadAttempted(true);

    try {
      console.log(`Auto-loading latest results for client: ${clientId}, workflow: ${workflowId}`);
      
      const results = await getLatestResults.mutateAsync({
        clientId,
        workflowId
      });
      
      if (results) {
        // Extract only what we need, with NO transformations
        const processedResults = extractRawResults(results);
        
        // Include any loaded keywords in the session storage for reuse
        if (results.originalKeywords && Array.isArray(results.originalKeywords)) {
          try {
            sessionStorage.setItem('latest_keywords', JSON.stringify(results.originalKeywords));
            console.log(`Stored ${results.originalKeywords.length} original keywords in session storage`);
          } catch (storageError) {
            console.warn('Could not store keywords in session storage:', storageError);
          }
        }
        
        console.log('Auto-loaded latest results successfully');
        onResultsLoaded(processedResults);
      } else {
        console.log('No previous results found for auto-loading');
      }
    } catch (error) {
      console.error('Error auto-loading latest results:', error);
      // Don't show error toast for auto-loading to avoid confusing users
    }
  };

  // Reset auto-load flag when workflow changes
  useEffect(() => {
    setAutoLoadAttempted(false);
  }, [clientId, workflowId]);

  // Auto-load latest results on component mount
  useEffect(() => {
    if (clientId && workflowId && onResultsLoaded && !autoLoadAttempted) {
      autoLoadLatestResults();
    }
  }, [clientId, workflowId, onResultsLoaded, autoLoadAttempted]);

  const handleLoadKeywords = async () => {
    if (!clientId || !workflowId) {
      showToast('Client ID and Workflow ID are required to load keywords', 'error');
      return;
    }

    try {
      setIsLoadingKeywords(true);
      showToast('Loading keywords from Snowflake...', 'info');

      // Call the new endpoint to load keywords and save to blob storage
      const result = await snowflakeService.loadKeywordsToBlob({
        client_id: clientId,
        workflow_id: workflowId
      });

      if (result.success && result.data) {
        showToast(
          `Successfully loaded ${result.data.keyword_count} keywords to blob storage`,
          'success'
        );
        
        // Mark that we have Snowflake keywords
        setHasSnowflakeKeywords(true);
        setSnowflakeBlobPath(result.data.blob_path);
        setSnowflakeKeywordCount(result.data.keyword_count);
        
        // Don't trigger analysis automatically - let the user submit the form
        console.log('Keywords loaded to blob storage. User can now configure and submit analysis.');
      } else {
        showToast(result.message || 'Failed to load keywords', 'error');
      }
    } catch (error: any) {
      console.error('Error loading keywords:', error);
      showToast('Failed to load keywords from Snowflake', 'error');
    } finally {
      setIsLoadingKeywords(false);
    }
  };

  return (
    <Grid container spacing={3}>
      {/* Left Column - Business Information */}
      <Grid item xs={12} md={8}>
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" fontWeight="bold" mb={0.5}>Business Information</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Enter your business information. Keywords will be analyzed from your uploaded file.
            </Typography>
            
            {/* Add Load Latest Results section */}
            {onShowResults && (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                    Quick Start
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {hasModule1Results 
                      ? "Show your existing analysis results if available."
                      : "No completed Module 1 analysis found for this workflow. Run a new analysis first."
                    }
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <LoadLatestResultsButton 
                      onResultsLoaded={onShowResults}
                      disabled={isProcessing || isUploading || !hasModule1Results}
                      clientId={clientId}
                      workflowId={workflowId}
                      hasResults={hasModule1Results}
                    />
                    {clientId && (
                      <Button
                        variant="contained"
                        startIcon={<CloudUpload />}
                        onClick={() => setFileUploadModalOpen(true)}
                        sx={{
                          borderRadius: '8px',
                          bgcolor: 'primary.main',
                          '&:hover': {
                            bgcolor: 'primary.dark',
                          }
                        }}
                      >
                        Upload Files
                      </Button>
                    )}
                  </Box>
                  
                  {/* Keywords Source */}
                  <Box sx={{ mt: 2 }}>
                    <Alert severity={hasUploadedFile || hasSnowflakeKeywords ? "success" : "info"} icon={<Upload />}>
                      <AlertTitle>Keywords Source</AlertTitle>
                      <Typography variant="body2">
                        {hasSnowflakeKeywords ? (
                          <>
                            <Box component="span" sx={{ display: 'block', mt: 0.5, color: 'success.main', fontWeight: 500 }}>
                              ✓ Keywords loaded from Snowflake
                              {snowflakeKeywordCount && (
                                <Box component="span" sx={{ ml: 1 }}>
                                  ({snowflakeKeywordCount} keywords)
                                </Box>
                              )}
                            </Box>
                          </>
                        ) : hasUploadedFile ? (
                          <Box component="span" sx={{ display: 'block', mt: 0.5, color: 'success.main', fontWeight: 500 }}>
                            ✓ File has been uploaded
                          </Box>
                        ) : (
                          <>
                            Please upload your keywords file using the Upload Files button above or load keywords from Snowflake.
                            <Box component="span" sx={{ display: 'block', mt: 0.5, color: 'error.main', fontWeight: 500 }}>
                              × No keywords loaded yet
                            </Box>
                          </>
                        )}
                      </Typography>
                    </Alert>
                  </Box>
                </Box>
                <Divider sx={{ mb: 3 }} />
                <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                  Or Run New Analysis
                </Typography>
              </>
            )}
            
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              <InputFormContainer 
                onAnalysisComplete={onAnalysisComplete} 
                className="h-full"
                hasUploadedFile={hasUploadedFile}
                hasSnowflakeKeywords={hasSnowflakeKeywords}
                isSubmitting={isProcessing}
                snowflakeBlobPath={snowflakeBlobPath}
                snowflakeKeywordCount={snowflakeKeywordCount}
                hideKeywordsSource={true}
                workflowId={workflowId}
                workflow={workflow}
              />
            </Box>
            
            {/* Keyword Storage Status */}
            <KeywordStorageStatus
              isProcessing={isProcessing}
              isUsingDatabaseApproach={isUsingDatabaseApproach}
              analysisComplete={false}
              storedKeywordCount={storedKeywordCount}
              totalKeywordCount={totalKeywordCount}
            />
          </CardContent>
        </Card>
      </Grid>
      
      {/* Right Column - Keyword Readiness Status */}
      <Grid item xs={12} md={4}>
        <KeywordReadinessStatus
          clientId={clientId}
          clientName={clientName}
          workflowId={workflowId}
          onStartProcess={handleLoadKeywords}
          isProcessing={isProcessing || isLoadingKeywords}
          refreshTrigger={refreshTrigger}
          disabled={isProcessing || isLoadingKeywords}
        />
      </Grid>
      
      {/* File Upload Modal */}
      {clientId && (
        <ClientFileUploadModal
          open={fileUploadModalOpen}
          onClose={() => {
            setFileUploadModalOpen(false);
            setRefreshTrigger(prev => prev + 1); // Trigger KeywordReadinessStatus refresh
          }}
          clientId={clientId}
          clientName={clientName}
          workflowId={workflowId}
          workflowName={workflowName}
        />
      )}
    </Grid>
  );
};

export default AnalysisInputs;
